import { apiClient } from '../utils/apiClient';
import { getSAProxyInvestorId, saProxyApiClient } from './saProxyAdapter';
import { getActiveInvestorJourney } from './investorJourneyScope';

export interface DocumentResponseDto {
  id?: number;
  documentType: string;
  documentCategory: string;
  fileName: string;
  documentUrl?: string;
  contentType?: string;
  fileSize?: number;
  status: string;
  uploadedAt?: string;
  description?: string;
  rejectionReason?: string;
}

// New Registration Flow DTOs
export interface EmailRegistrationDto {
  email: string;
  registerAs: number; // 1=Individual, 2=Legal Entity
  consentGiven?: boolean; // Data consent accepted on the page before the email step
}

export interface EmailRegistrationResponseDto {
  success: boolean;
  message: string;
  uniqueCode?: string;
  emailAlreadyExists?: boolean;
  otpSent?: boolean;
  expiresInMinutes?: number;
}

export interface IndividualRegistrationDto {
  interestedInIndianMarket: boolean;
  title?: number; // Optional: Reference to master_title
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  nationality: number;
  countryOfResidence: number;
  hasPanCard?: boolean; // no longer asked at self-registration
  residencyType?: string; // "resident_indian" or "non_resident_indian"
  isPersonOfIndianOrigin?: boolean;
  hasOciCard?: boolean;
  termsAccepted: boolean;
}

export interface LegalEntityRegistrationDto {
  entityName: string;
  countryOfIncorporation: number;
  hasPanCard?: boolean;
  entityRepresentativeName: string;
  representativeCapacity: string;
  isSecuritiesRegulated: boolean;
  termsAccepted: boolean;
}

export interface RegistrationCompletionResponseDto {
  success: boolean;
  message: string;
  uniqueCode?: string;
  investorId?: number;
  email?: string;
  requiresPanCard?: boolean;
  /** B2C object id when the Azure user was created — the success page offers
   *  "Set Your Password" directly with it (introduced-flow parity). */
  azureUserId?: string;
}

// Legacy DTOs (keeping for backward compatibility)
export interface MainStepDto {
  fullName: string;
  registerAs: number;
  market: number;
  countryOfIncorporation?: number;
  countryOfTaxResidency?: number;
  entityWebsite?: string;
}

export interface Step1Dto {
  title: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  whatsappNumber?: string;
  countryCode: number;
  gender?: number;
  userDob?: string;
  representativeCapacity?: string;
}

export interface OtpVerificationDto {
  uniqueCode: string;
  emailOtp: string;
  smsOtp: string;
}

export interface Step3RegistrationDetailsDto {
  citizenship: number;
  countryOfResidence: number;
  nationality: number;
  residenceType: string;
  pancardStatus?: string;
  ociCardStatus?: string;
  indianOrigin?: string;
  confirmation: boolean;
  privacyPolicyAccepted: boolean;
  notificationConsent: boolean;
  whatsappConsent?: boolean;
}

export interface OtpResponseDto {
  message: string;
  emailSent: boolean;
  smsSent: boolean;
  expiresInMinutes: number;
}

export interface VerificationResponseDto {
  success: boolean;
  message: string;
  investorId?: number;
  uniqueCode?: string;
}

export interface SectionStatus {
  completed: boolean;
  required: boolean;
  lastUpdated?: string;
}

export interface InvestorProgressDto {
  currentStep: string;
  completedSteps: string[];
  pendingSteps: string[];
  progressPercentage: number;
  sections: Record<string, SectionStatus>;
}

export interface InvestorBasicInfo {
  name: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  nationality: string;
  countryOfResidence: string;
  /**
   * `master_countries.id` for `countryOfResidence`, or null when the backend could not map it.
   * `countryOfResidence` is a display name from Dataverse / master_country_of_residence, whose
   * spellings differ from the master_countries list behind our country dropdowns ("United States
   * of America" vs "United States"). Preselect by this id — never by matching the name string.
   */
  countryOfResidenceId?: number | null;
  investorType: string;
  uniqueCode: string;
  status: number;
  registerAs: string;
}

export interface ActivityItem {
  action: string;
  timestamp: string;
  description: string;
}

export interface InvestorDashboardDto {
  investor: InvestorBasicInfo;
  progress: InvestorProgressDto;
  recentActivity: ActivityItem[];
  nextSteps: string[];
  accountSummary?: AccountSummary; // Option 2: Enhanced dashboard
  accountSnapshot?: AccountSnapshot;
  actionsAlerts?: ActionsAlerts;
  productAssignment?: ProductAssignment;
  applications?: ApplicationItem[];
  consentCenter?: ConsentItem[];
  /** Incomplete Facilon-status steps for the "My Pending Action" card. */
  pendingActions?: PendingActionDto[];
  delegation?: DelegationInfo;
  /** True once the investor has agreed to their SOW. The onboarding journey is gated on this. */
  sowAgreed?: boolean;
}

export interface PendingActionDto {
  activity?: string;     // step label, e.g. "KYC Docs"
  centre?: string;       // CENTRA column grouping, e.g. "Compliance"
  status?: string;       // "PENDING" (current step) | "REQUIRED" (not started)
  stepKey?: string;      // information|documents|onboarding|verification|physical|account
  actionRoute?: string;  // frontend route to act on this step
}

export interface AccountSnapshot {
  investorId?: string;
  primaryJurisdiction?: string;
  eligibility?: string;
  lastActivity?: string;
}

export interface ActionsAlerts {
  actionRequired?: string;
  onboardingStatus?: string;
  restrictions?: string;
}

export interface ProductAssignment {
  assigned?: boolean;
  source?: string;
  serviceProviderName?: string;
  serviceProviderType?: string;
  productName?: string;
  productCode?: string;
  planName?: string;
  routeName?: string;
  message?: string;
}

export interface ApplicationItem {
  code?: string;
  name?: string;
  status?: string;
  enabled?: boolean;
  blockReason?: string;
  actionRoute?: string;
}

export interface ConsentItem {
  consent?: string;
  scope?: string;
  status?: string; // "Active" | "Inactive"
  actionRequired?: boolean;
  /** A response was delivered — tag shown on Closed rows (task E). */
  responseAvailable?: boolean;
  action?: string; // "ACTIVATE" | "UPDATE" | "REVOKE" | "NONE"
  key?: string;    // "sow" | "marketing" | "whatsapp" | "privacy" | "platformTerms"
}

export interface DelegationInfo {
  serviceAgent?: string;
  scope?: string;
  expiry?: string;
  status?: string;
  note?: string;
}

export interface DsrCaseCreateDto {
  requestType: string;
  jurisdiction: string;
  dataArea?: string;
  requestDescription: string;
  requesterName: string;
  requesterEmail: string;
  requesterPhone?: string;
  requesterRole?: string;
}

export interface DsrCaseResponseDto {
  caseId: string;
  investorUniqueCode: string;
  requestType: string;
  jurisdiction: string;
  dataArea?: string;
  requestDescription: string;
  requesterName: string;
  requesterEmail: string;
  requesterPhone?: string;
  requesterRole?: string;
  /** Plain-language, investor-facing status (mapped from internal status). */
  investorStatus: string;
  actionRequired: boolean;
  /** A response was delivered — tag shown on Closed rows (task E). */
  responseAvailable?: boolean;
  supportingFilePath?: string;
  submittedAt?: string;
  slaDeadline?: string;
  resolvedAt?: string;
  resolutionNotes?: string;
  finalOutcome?: string;
}

export interface DsrCaseEventDto {
  eventCode: string;
  title: string;
  note?: string;
  createdAt?: string;
}

export interface DsrCaseDetailDto {
  request: DsrCaseResponseDto;
  timeline: DsrCaseEventDto[];
}

export interface AccountSummary {
  accountOpeningStatus: boolean;
  verificationDone: boolean;
  physicalSubmissionDone: boolean;
  kycDocumentsUploaded: number;
  kycDocumentsRequired: number;
  /** Approved KYC docs, clamped to required. Gates onboarding. */
  kycDocumentsApproved?: number;
  onboardingDocumentsUploaded: number;
  onboardingDocumentsRequired: number;
  onboardingDocumentsApproved?: number;
  bankInfo?: BankInfo;
}

export interface BankInfo {
  bankName: string;
  accountNumber: string; // Masked
  ifscCode: string;
  hasBankDetails: boolean;
}

export interface VerificationStatusDto {
  currentStatus: string; // "pending", "in_progress", "completed", "rejected"
  physicalSubmission: PhysicalSubmissionInfo;
  appointment: VerificationAppointmentInfo;
  verifiedBy?: string;
  verifiedAt?: string;
  rejectionReason?: string;
  nextSteps: string;
  /** Booking page for the service provider owning the journey in view. */
  ipvUrl?: string;
}

export interface PhysicalSubmissionInfo {
  submitted: boolean;
  submittedAt?: string;
  trackingNumber?: string;
  status: string;
  // Additional fields for form pre-filling
  physicalSubmission?: 'inperson' | 'courier';
  courierName?: string;
  dispatchDate?: string;
}

export interface VerificationAppointmentInfo {
  scheduled: boolean;
  appointmentDate?: string;
  appointmentTime?: string;
  verificationType?: string;
  location?: string;
  status: string;
}

export interface VerificationAppointmentDto {
  appointmentDate: string;
  appointmentTime: string;
  verificationType: string;
  location?: string;
  notes?: string;
}

export interface RequiredDocument {
  dynamicsId: string;
  description: string;
  documentTypeCode?: string;
  documentType?: number; // Numeric ss_documenttype from Dataverse (e.g., 100000012 for downloadable forms)
  mandatory?: boolean;
  localRecordExists?: boolean;
  localStatus?: string;
  reason?: string;
  localDocumentId?: number;
  fileName?: string;
  documentUrl?: string;
  documentMasterUrl?: string; // SharePoint URL from ss_documentmasters.ss_documenturl (for downloadable templates)
  uploadedAt?: string;
  acceptedFormats?: string[] | string;
  maxSize?: string;
  inputId?: string;
  spanId?: string;
  errorId?: string;
  fileInputName?: string;
}

export interface KycDocumentRequirementDto {
  totalRequired: number;
  uploaded: number;
  pending: number;
  approved: number;
  rejected: number;
  completionPercentage: number;
  serviceProviderName?: string;
  documents: RequiredDocument[];
}

export interface BankAccountDetails {
  // Standard bank details (from Dataverse ss_productsvisitorses)
  bankName?: string; // ss_nameofbank
  branchAddress?: string; // ss_bankaddress
  accountNumber?: string; // ss_bankaccountnumber (masked)
  swiftCode?: string; // ss_swiftcode
  ifscCode?: string; // ss_ifsccode
  
  // Specialized account numbers (from Dataverse)
  safeKeepingAccountNo?: string; // ss_safekeepingcustodyaccountno
  tradingAccountNo?: string; // ss_tradingaccountno
  pmsAccountFolioNo?: string; // ss_pmsaccountfoliono
  
  // Depository account numbers (NEW - from Dataverse ss_productsvisitorses)
  nsdlDpId?: string; // ss_depositorynsdldpid
  nsdlAccountNo?: string; // ss_depositorynsdlaccountno
  cdslAccountNo?: string; // ss_depositorycsdlaccountno
  
  accountType?: string;
  isPrimary?: boolean;
}

export interface ProgressInfo {
  personalInfoComplete: boolean;
  passportComplete: boolean;
  residentialComplete: boolean;
  taxInfoComplete: boolean;
  bankDetailsComplete: boolean;
  contactDetailsComplete: boolean;
  nominationComplete: boolean;
  riskProfileComplete: boolean;
  kycDocumentsUploaded: number;
  kycDocumentsRequired: number;
  /** Approved KYC docs, clamped to required. Gates onboarding. */
  kycDocumentsApproved?: number;
  onboardingDocumentsUploaded: number;
  onboardingDocumentsRequired: number;
  onboardingDocumentsApproved?: number;
  verificationDone: boolean;
  verificationDoneBy?: string;
  verificationDateTime?: string;
  physicalSubmissionDone: boolean;
  physicalSubmissionMethod?: string;
  courierName?: string;
  dispatchDate?: string;
  awbNumber?: string;
  completionPercentage: number;
}

export interface AccountDetailsDto {
  uniqueCode: string;
  fullName: string;
  email: string;
  mobile: string;
  registrationType: string; // Self, Introduced, PMS
  investorCategory?: string;
  investorCategoryCode: string;
  registrationDate: string;
  lastUpdated: string;

  // Account Opening Status
  accountOpeningStatus: boolean; // true = Completed, false/null = Pending

  // Progress tracking
  progress: ProgressInfo;

  // Bank and Account Details
  bankAccount?: BankAccountDetails;
}

export interface SetPasswordDetailsDto {
  azureUserId: string;
  b2cTenantName: string;
  clientId: string;
  signupSigninPolicy: string;
  resetPasswordPolicy: string;
  redirectUri: string;
  scope: string;
  hasLoggedInBefore: boolean;
  displayName: string;
}


export interface JourneyListItem {
  journeyId?: string;
  serviceProviderName?: string;
  /** Dataverse SP GUID (matches ServiceProviderDto.spRef). Null for PMS/legacy — never filtered out. */
  spRef?: string | null;
  product?: string;
  productCode?: string;
  scheme?: string;
  plan?: string;
  status?: string;
  progress?: number;
  actionRoute?: string;
}

/**
 * Person layer (Phase 3): one dashboard account group — the person and their journeys.
 * Today the API returns exactly one group; family linking later appends view-only groups.
 */
export interface PersonAccountGroup {
  personId: number | null;
  displayName: string;
  identityReview?: boolean;
  journeys: JourneyListItem[];
}

/** UX task B: one pending consent/permission row for the dashboard card. */
export interface PendingActionRow {
  type: 'SERVICE_AGENT_CONSENT' | 'PRODUCT_APPROVAL' | 'SOW_CONSENT' | 'KYC_CONSENT' | 'PLATFORM_CONSENT'
    | 'JOURNEY_SUBMIT_REQUIRED';
  productName?: string;
  pendingItem?: string;
  serviceProviderName?: string | null;
  personId?: number | null;
  personName?: string | null;
  journeyId?: string | null;
  refId?: string | null;
}

export interface JourneyKycGate {
  sowAgreed: boolean;
  kycComplete: boolean;
  consentGiven: boolean;
  /** F-11: the investor already skipped Smart Upload for this journey — resume into the journey. */
  smartUploadSkipped?: boolean;
}

export interface JourneyKycConsentConflict {
  targetField: string;
  existingValue: string | null;
  documentValue: string | null;
  reason: string;
}

export interface JourneyKycConsentResult {
  addressConflict: boolean;
  conflicts: JourneyKycConsentConflict[];
}

class InvestorService {
  private readonly baseUrl = '/api/clients';

  private get client() {
    return getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
  }

  /**
   * P3.3: the investor's active journey, appended as {@code journeyId} to per-journey
   * (onboarding-document + physical-submission) calls. Returns {@code undefined} in SA proxy mode —
   * there the proxy already tags {@code selectedJourneyId} and the investor's own store must not leak
   * in. {@code undefined} ⇒ backend falls back to the primary journey (single-journey investors).
   */
  private ownJourneyId(): string | undefined {
    if (getSAProxyInvestorId() !== null) return undefined;
    return getActiveInvestorJourney() ?? undefined;
  }

  /** Axios `params` object carrying the active journey, or undefined when there is none. */
  private journeyParams(): { journeyId: string } | undefined {
    const jid = this.ownJourneyId();
    return jid ? { journeyId: jid } : undefined;
  }

  // New Registration Flow APIs
  async submitEmail(data: EmailRegistrationDto): Promise<EmailRegistrationResponseDto> {
    const response = await this.client.post<EmailRegistrationResponseDto>(
      `${this.baseUrl}/onboarding/register/email`,
      data
    );
    return response.data;
  }

  async completeIndividualRegistration(uniqueCode: string, data: IndividualRegistrationDto): Promise<RegistrationCompletionResponseDto> {
    const response = await this.client.post<RegistrationCompletionResponseDto>(
      `${this.baseUrl}/onboarding/register/individual/${uniqueCode}`,
      data
    );
    return response.data;
  }

  async completeLegalEntityRegistration(uniqueCode: string, data: LegalEntityRegistrationDto): Promise<RegistrationCompletionResponseDto> {
    const response = await this.client.post<RegistrationCompletionResponseDto>(
      `${this.baseUrl}/onboarding/register/legal-entity/${uniqueCode}`,
      data
    );
    return response.data;
  }

  /**
   * Returns the B2C config + sign-in history needed by the FISP-style setpassword page.
   * The Azure object id comes from the first-login email link.
   */
  async getSetPasswordDetails(azureUserId: string): Promise<SetPasswordDetailsDto> {
    const response = await this.client.get<SetPasswordDetailsDto>(
      `${this.baseUrl}/onboarding/setpassword/details/${encodeURIComponent(azureUserId)}`
    );
    return response.data;
  }

  // Legacy APIs (keeping for backward compatibility)
  async registerMainStep(data: MainStepDto): Promise<string> {
    const response = await this.client.post<string>(
      `${this.baseUrl}/onboarding/register/main-step`,
      data
    );
    return response.data;
  }

  async registerStep1(code: string, data: Step1Dto): Promise<OtpResponseDto> {
    const response = await this.client.post<OtpResponseDto>(
      `${this.baseUrl}/onboarding/register/step1/${code}`,
      data
    );
    return response.data;
  }

  async verifyOtp(data: OtpVerificationDto): Promise<VerificationResponseDto> {
    const response = await this.client.post<VerificationResponseDto>(
      `${this.baseUrl}/onboarding/register/step2/verify-otp`,
      data
    );
    return response.data;
  }

  async registerStep3(code: string, data: Step3RegistrationDetailsDto): Promise<VerificationResponseDto> {
    const response = await this.client.post<VerificationResponseDto>(
      `${this.baseUrl}/onboarding/register/step3/${code}`,
      data
    );
    return response.data;
  }

  async getProgress(): Promise<InvestorProgressDto> {
    const response = await this.client.get<InvestorProgressDto>(
      `${this.baseUrl}/me/progress`
    );
    return response.data;
  }

  /**
   * A1: pass a `selectedJourneyId` to scope the Account Summary + Pending Actions cards to that
   * journey (undefined = identity-level default view). The SA proxy adapter rewrites `/me/dashboard`
   * to the agent endpoint automatically, so the param flows through for both investor and SA views.
   */
  async getDashboard(selectedJourneyId?: string): Promise<InvestorDashboardDto> {
    // Explicit arg wins (dashboard account selector); otherwise fall back to the investor's active
    // journey so pages without a route param (onboarding, physical) still get the opened journey's
    // header + stepper. undefined in SA proxy mode → the proxy appends its own selectedJourneyId.
    const jid = selectedJourneyId ?? this.ownJourneyId();
    const response = await this.client.get<InvestorDashboardDto>(
      `${this.baseUrl}/me/dashboard`,
      jid ? { params: { selectedJourneyId: jid } } : undefined
    );
    return response.data;
  }

  async getJourneys(): Promise<JourneyListItem[]> {
    const response = await this.client.get<JourneyListItem[]>(
      `${this.baseUrl}/me/journeys`
    );
    return response.data;
  }

  /** Person-grouped accounts for the dashboard selector (login-derived; never proxied). */
  async getPersonAccounts(): Promise<PersonAccountGroup[]> {
    const response = await this.client.get<PersonAccountGroup[]>(
      `${this.baseUrl}/me/persons`
    );
    return response.data;
  }

  /** UX task B: pending consents/permissions across the account (person-labelled). */
  async getPendingActions(): Promise<PendingActionRow[]> {
    const response = await this.client.get<PendingActionRow[]>(`${this.baseUrl}/me/pending-actions`);
    return response.data;
  }

  // Phase 3: per-journey KYC-reuse consent gate.
  async getJourneyKycGate(journeyId: string): Promise<JourneyKycGate> {
    const response = await this.client.get<JourneyKycGate>(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/kyc-gate`
    );
    return response.data;
  }

  /** F-11: record a Smart-Upload skip for this journey (any of the three exits). */
  async skipSmartUpload(journeyId: string): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/journeys/${journeyId}/smart-upload-skip`);
  }

  /**
   * Discontinue (abandon) a journey: Dataverse ss_abandonproduct is patched first so the
   * Service Provider sees it, then the local records flip to ABANDONED. Idempotent.
   */
  async discontinueJourney(journeyId: string, reason?: string): Promise<void> {
    await this.client.post(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/discontinue`,
      reason && reason.trim() ? { reason: reason.trim() } : {}
    );
  }

  // "I Agree": records consent, auto-fills the profile from confirmed docs, archives them.
  // Returns any conflicts so the caller can offer a reconcile prompt (e.g. use latest address).
  async giveJourneyKycConsent(journeyId: string): Promise<JourneyKycConsentResult> {
    const response = await this.client.post<JourneyKycConsentResult>(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/kyc-consent`
    );
    return response.data;
  }

  // §6.4: is a CONFIRMED KYC document newer than this journey's last fill?
  // Used to OFFER an update — never to apply one automatically.
  async isKycRefreshAvailable(journeyId: string): Promise<boolean> {
    const response = await this.client.get<{ available: boolean }>(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/kyc-refresh`
    );
    return response.data?.available === true;
  }

  // §6.4: investor accepted — re-fill THIS journey from the newer documents.
  // Consent is not re-asked; conflicts come back for the same reconcile prompt.
  async applyKycRefresh(journeyId: string): Promise<JourneyKycConsentResult> {
    const response = await this.client.post<JourneyKycConsentResult>(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/kyc-refresh`
    );
    return response.data;
  }

  // Records a "Skip for now" decision so the consent prompt is asked only once.
  async skipJourneyKycConsent(journeyId: string): Promise<void> {
    await this.client.post(
      `${this.baseUrl}/me/journeys/${encodeURIComponent(journeyId)}/kyc-skip`
    );
  }

  // Investor approved "use the latest address" in the reconcile prompt.
  async applyLatestKycAddress(): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/kyc/address/apply-latest`);
  }

  // Streams a KYC document's bytes (resolves SharePoint or decrypts the Blob copy).
  // Goes through the authenticated client so the JWT is attached.
  async downloadDocument(documentId: number): Promise<Blob> {
    const response = await this.client.get(
      `${this.baseUrl}/me/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  }

  async submitDsrCase(data: DsrCaseCreateDto, supportingFiles?: File[]): Promise<DsrCaseResponseDto> {
    const formData = new FormData();
    formData.append('requestType', data.requestType);
    formData.append('jurisdiction', data.jurisdiction);
    if (data.dataArea) formData.append('dataArea', data.dataArea);
    formData.append('requestDescription', data.requestDescription);
    formData.append('requesterName', data.requesterName);
    formData.append('requesterEmail', data.requesterEmail);
    if (data.requesterPhone) formData.append('requesterPhone', data.requesterPhone);
    if (data.requesterRole) formData.append('requesterRole', data.requesterRole);
    (supportingFiles || []).forEach((file) => formData.append('supportingFiles', file));

    const response = await this.client.post<DsrCaseResponseDto>(`${this.baseUrl}/me/dsr`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getDsrCases(): Promise<DsrCaseResponseDto[]> {
    const response = await this.client.get<DsrCaseResponseDto[]>(`${this.baseUrl}/me/dsr`);
    return response.data;
  }

  async getDsrCase(caseId: string): Promise<DsrCaseDetailDto> {
    const response = await this.client.get<DsrCaseDetailDto>(`${this.baseUrl}/me/dsr/${caseId}`);
    return response.data;
  }

  async submitPersonalInformation(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/personal`, data);
  }

  async submitPassportInformation(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/passport`, data);
  }

  async submitResidentialStatus(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/residential`, data);
  }

  async submitTaxInformation(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/tax`, data);
  }

  async submitBankDetails(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/bank`, data);
  }

  async submitContactDetails(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/contact`, data);
  }

  async submitNominationDetails(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/nomination`, data);
  }

  async submitRiskProfile(data: any): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/risk-profile`, data);
  }

  async finalInformationSubmit(): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/information/final-submit`);
  }

  async uploadKycDocument(file: File, documentType: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    // Stamp the journey so the document counts only for that service provider.
    const jid1 = this.ownJourneyId();
    if (jid1) formData.append('journeyId', jid1);

    const response = await this.client.post(`${this.baseUrl}/me/documents/kyc`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadDocument(file: File, documentType: string, documentMasterId?: string): Promise<DocumentResponseDto> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (documentMasterId) {
      formData.append('documentMasterId', documentMasterId);
    }
    const jid2 = this.ownJourneyId();
    if (jid2) formData.append('journeyId', jid2);

    const response = await this.client.post<DocumentResponseDto>(`${this.baseUrl}/me/documents/kyc`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getKycRequirements(): Promise<KycDocumentRequirementDto> {
    // Journey-scoped: an investor may work with several service providers, so the
    // heading's SP name has to come from the journey being viewed.
    const response = await this.client.get<KycDocumentRequirementDto>(
      `${this.baseUrl}/me/documents/kyc/requirements`, { params: this.journeyParams() });
    return response.data;
  }

  async getOnboardingRequirements(): Promise<KycDocumentRequirementDto> {
    const response = await this.client.get<KycDocumentRequirementDto>(
      `${this.baseUrl}/me/documents/onboarding/requirements`,
      { params: this.journeyParams() }
    );
    return response.data;
  }

  async getDocuments(): Promise<any[]> {
    const response = await this.client.get(`${this.baseUrl}/me/documents`);
    return response.data as any[];
  }

  async getDocumentStatus(documentId: number): Promise<any> {
    const response = await this.client.get(`${this.baseUrl}/me/documents/${documentId}/status`);
    return response.data;
  }

  async updateAppointmentStatus(appointmentId: number, status: string): Promise<void> {
    await this.client.put(`${this.baseUrl}/me/verification/appointment/${appointmentId}/status`, null, {
      params: { status },
    });
  }

  async getVerificationStatus(): Promise<VerificationStatusDto> {
    const response = await this.client.get<VerificationStatusDto>(
      `${this.baseUrl}/me/verification/status`,
      { params: this.journeyParams() }
    );
    return response.data;
  }

  async scheduleVerificationAppointment(data: VerificationAppointmentDto): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/verification/appointment`, data);
  }

  async getOnboardingDocuments(): Promise<DocumentResponseDto[]> {
    const response = await this.client.get<DocumentResponseDto[]>(
      `${this.baseUrl}/me/documents/onboarding/list`,
      { params: this.journeyParams() }
    );
    return response.data;
  }

  /**
   * @param investorDocumentId Dataverse ss_investordocumentsid (from onboarding requirements dynamicsId). Required when API has Dataverse enabled.
   */
  async uploadOnboardingDocument(
    file: File,
    documentType: string,
    investorDocumentId?: string
  ): Promise<DocumentResponseDto> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (investorDocumentId) {
      formData.append('investorDocumentId', investorDocumentId);
    }
    const journeyId = this.ownJourneyId();
    if (journeyId) {
      formData.append('journeyId', journeyId);
    }

    const response = await this.client.post<DocumentResponseDto>(`${this.baseUrl}/me/documents/onboarding`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async downloadPreFilledForms(): Promise<any> {
    const response = await this.client.get(`${this.baseUrl}/me/documents/onboarding/forms/download`);
    return response.data;
  }

  /**
   * Download SharePoint document by URL (for onboarding templates)
   * @param url SharePoint document URL (from ss_doc_master_url)
   * @returns Blob containing the file
   */
  async downloadSharePointDocument(url: string): Promise<Blob> {
    const response = await this.client.get(
      `${this.baseUrl}/me/documents/sharepoint/download`,
      {
        params: { url },
        responseType: 'blob'
      }
    );
    return response.data;
  }

  async deleteDocument(documentId: number): Promise<void> {
    await this.client.delete(`${this.baseUrl}/me/documents/${documentId}`);
  }

  async getAccountDetails(): Promise<AccountDetailsDto> {
    // Journey-scoped: bank / trading / depository numbers live on the journey's own
    // ss_investorproducts record, so without this every journey showed the primary one's.
    const response = await this.client.get<AccountDetailsDto>(
      `${this.baseUrl}/me/account-details`,
      { params: this.journeyParams() }
    );
    return response.data;
  }

  async submitPhysicalDocuments(data: PhysicalSubmissionFormData): Promise<void> {
    await this.client.post(`${this.baseUrl}/me/verification/physical-submission`, data, {
      params: this.journeyParams(),
    });
  }

  async getPhysicalSubmissionStatus(): Promise<PhysicalSubmissionInfo | null> {
    try {
      const verificationStatus = await this.getVerificationStatus();
      return verificationStatus.physicalSubmission || null;
    } catch (error) {
      console.warn('[InvestorService] Failed fetching physical submission status', error);
      return null;
    }
  }

  async downloadDocumentChecklist(): Promise<string> {
    // Journey-scoped: the checklist prints THIS journey's provider, product and uploaded
    // documents — without the journey every journey downloaded the same identity-level list.
    const response = await this.client.get(`${this.baseUrl}/me/verification/document-checklist`, {
      responseType: 'text',
      params: this.journeyParams()
    });
    return response.data;
  }
}

export interface PhysicalSubmissionFormData {
  physicalSubmission: 'inperson' | 'courier';
  courierName?: string;
  dispatchDate?: string;
  awbNumber?: string;
  notes?: string;
}

export const investorService = new InvestorService();
